import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Remove, DragIndicator } from "@mui/icons-material";
import ThumbnailImage from "./ThumbnailImage";
import { Video } from "../api/types";

interface VideoItemProps {
  video: Video;
  index: number;
  showRemoveButton?: boolean;
  showDragHandle?: boolean;
  onRemove?: (videoToken: string) => void;
  dragProps?: {
    attributes?: any;
    listeners?: any;
    setNodeRef?: (node: HTMLElement | null) => void;
    style?: React.CSSProperties;
  };
  containerStyle?: React.CSSProperties;
}

const VideoItem: React.FC<VideoItemProps> = ({
  video,
  index,
  showRemoveButton = false,
  showDragHandle = false,
  onRemove,
  dragProps = {},
  containerStyle = {},
}) => {
  const { attributes, listeners, setNodeRef, style: dragStyle } = dragProps;

  return (
    <Box
      ref={setNodeRef}
      style={dragStyle}
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "0.5em",
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "white",
        cursor: "default",
        "&:hover": {
          backgroundColor: "#f5f5f5",
          ...(showRemoveButton && {
            "& .remove-button": {
              opacity: 1,
            },
            "& .number-text": {
              opacity: 0,
            },
          }),
        },
        ...containerStyle,
      }}
    >
      {/* Number/Remove */}
      <Box
        sx={{
          width: 50,
          textAlign: "center",
          position: "relative",
          ...(showRemoveButton && {
            "& .remove-button": {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              opacity: 0,
              transition: "opacity 0.2s",
            },
          }),
        }}
      >
        <Typography
          className="number-text"
          variant="body2"
          color="text.secondary"
          sx={{ transition: "opacity 0.2s" }}
        >
          {index + 1}
        </Typography>
        {showRemoveButton && onRemove && (
          <IconButton
            className="remove-button"
            size="small"
            onClick={() => onRemove(video.token)}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: "rgba(0,0,0,0.1)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.2)",
              },
            }}
          >
            <Remove fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Thumbnail */}
      <Box sx={{ width: 100, textAlign: "center" }}>
        <ThumbnailImage
          src={video?.default_stream?.image_url}
          alt={video.title}
        />
      </Box>

      {/* Title */}
      <Box sx={{ flex: 1, paddingLeft: 2 }}>
        <Typography sx={{ fontWeight: "normal", fontSize: "12px" }}>
          {video.title}
        </Typography>
      </Box>

      {/* Drag Handle */}
      {showDragHandle && (
        <Box sx={{ width: 50, textAlign: "center" }}>
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab" }}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default VideoItem;
